import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { Translate, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { ITour } from 'app/shared/model/tour.model';
import { getEntities as getTours } from 'app/entities/tour/tour.reducer';
import { IPromotion } from 'app/shared/model/promotion.model';
import { getEntity, updateEntity, createEntity, reset } from './promotion.reducer';

export const PromotionUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const tours = useAppSelector(state => state.tour.entities);
  const promotionEntity = useAppSelector(state => state.promotion.entity);
  const loading = useAppSelector(state => state.promotion.loading);
  const updating = useAppSelector(state => state.promotion.updating);
  const updateSuccess = useAppSelector(state => state.promotion.updateSuccess);

  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClose = () => {
    navigate('/promotion' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
      setFormData({});
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getTours({}));
  }, []);

  useEffect(() => {
    if (promotionEntity) {
      setFormData({
        ...promotionEntity,
        tours: promotionEntity?.tours?.map(e => e.id.toString()),
      });
    }
  }, [promotionEntity]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.code || formData.code.trim() === '') {
      newErrors.code = 'Code is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleMultiSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleSwitchChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.checked });
  };

  const renderMultiSelectValue = (selected: any[]) => {
    return selected?.join(', ') || '';
  };

  // eslint-disable-next-line complexity
  const saveEntity = () => {
    if (!validate()) {
      return;
    }

    const values = { ...formData };

    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    if (values.discount !== undefined && typeof values.discount !== 'number') {
      values.discount = Number(values.discount);
    }

    const entity = {
      ...promotionEntity,
      ...values,
      tours: values.tours ? mapIdList(values.tours) : [],
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2">
          <Translate contentKey="jhipsterSampleApplicationApp.promotion.home.createOrEditLabel">Create or edit a Promotion</Translate>
        </Typography>
      </Box>
      <Box component="form" sx={{ '& .MuiTextField-root': { mb: 2 } }} noValidate autoComplete="off">
        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Box sx={{ '& .MuiTextField-root': { mb: 2 } }}>
            {!isNew ? (
              <TextField id="promotion-id" name="id" label={translate('global.field.id')} value={formData.id || ''} fullWidth InputProps={{ readOnly: true }} />
            ) : null}
            <TextField
              label={translate('jhipsterSampleApplicationApp.promotion.code')}
              id="promotion-code"
              name="code"
              data-cy="code"
              value={formData.code || ''}
              onChange={handleChange('code')}
              fullWidth
              required
              error={!!errors.code}
              helperText={errors.code || ''}
            />
            <FormControlLabel
              control={
                <Switch
                  id="promotion-enabled"
                  name="enabled"
                  data-cy="enabled"
                  checked={formData.enabled || false}
                  onChange={handleSwitchChange('enabled')}
                />
              }
              label={translate('jhipsterSampleApplicationApp.promotion.enabled')}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.promotion.discount')}
              id="promotion-discount"
              name="discount"
              data-cy="discount"
              value={formData.discount || ''}
              onChange={handleChange('discount')}
              fullWidth
              type="number"
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.promotion.fromDate')}
              id="promotion-fromDate"
              name="fromDate"
              data-cy="fromDate"
              value={formData.fromDate || ''}
              onChange={handleChange('fromDate')}
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.promotion.toDate')}
              id="promotion-toDate"
              name="toDate"
              data-cy="toDate"
              value={formData.toDate || ''}
              onChange={handleChange('toDate')}
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth>
              <InputLabel id="promotion-tours-label">{translate('jhipsterSampleApplicationApp.promotion.tour')}</InputLabel>
              <Select
                labelId="promotion-tours-label"
                id="promotion-tours"
                data-cy="tours"
                multiple
                value={formData.tours || []}
                label={translate('jhipsterSampleApplicationApp.promotion.tour')}
                onChange={handleMultiSelectChange('tours')}
                renderValue={renderMultiSelectValue}
              >
                {tours
                  ? tours.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                component={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/promotion"
                replace
                color="info"
                variant="outlined"
                startIcon={<FontAwesomeIcon icon="arrow-left" />}
              >
                <Translate contentKey="entity.action.back">Back</Translate>
              </Button>
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                onClick={saveEntity}
                disabled={updating}
                variant="contained"
                startIcon={<FontAwesomeIcon icon="save" />}
              >
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PromotionUpdate;
